import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Breadcrumbs,
  Button,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk } from '@/api/queries';
import type { AdminCollection, CollectionItem, CollectionKind, LabelKind } from '@/api/types';
import { Artwork } from '@/components/Artwork';
import { EmptyState } from '@/components/EmptyState';
import { ProgrammeAddSelect } from '@/components/pickers';
import { QueryState } from '@/components/QueryState';
import { SortableList } from '@/components/SortableList';
import { StatusBadge } from '@/components/StatusBadge';
import { toDate, toIso } from '@/lib/format';
import { COLLECTION_KINDS, LABEL_KINDS } from './collectionMeta';

export function CollectionEditorPage() {
  const { id = '' } = useParams();
  const query = useQuery({
    queryKey: qk.collection(id),
    queryFn: () => adminApi.getCollection(id),
  });
  return (
    <QueryState query={query}>{(c) => <CollectionEditor key={c.id} collection={c} />}</QueryState>
  );
}

function CollectionEditor({ collection }: { collection: AdminCollection }) {
  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/collections" size="sm">
          Collections
        </Anchor>
        <Text size="sm">{collection.title}</Text>
      </Breadcrumbs>
      <Title order={1}>{collection.title}</Title>
      {collection.archived && (
        <Alert color="gray" variant="light">
          This collection is archived and isn’t shown anywhere.
        </Alert>
      )}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <ItemsEditor collection={collection} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <SettingsForm collection={collection} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function ItemsEditor({ collection }: { collection: AdminCollection }) {
  const qc = useQueryClient();
  const [items, setItems] = useState<CollectionItem[]>(collection.items ?? []);
  const server = (collection.items ?? []).map((i) => i.programme_id).join(',');
  const dirty = items.map((i) => i.programme_id).join(',') !== server;

  useEffect(() => {
    setItems(collection.items ?? []);
  }, [collection.items]);

  const save = useMutation({
    mutationFn: () =>
      adminApi.setCollectionItems(
        collection.id,
        items.map((i) => i.programme_id),
      ),
    onSuccess: (c) => {
      qc.setQueryData(qk.collection(c.id), c);
      void qc.invalidateQueries({ queryKey: ['collections'] });
      void qc.invalidateQueries({ queryKey: ['programme'] });
      notifications.show({ color: 'leaf', message: 'Order saved.' });
    },
    onError: (e) =>
      notifications.show({ color: 'coral', title: 'Not saved', message: errorMessage(e) }),
  });

  return (
    <Paper withBorder p="md" radius="md" className="pb-surface">
      <Stack gap="sm">
        <Group justify="space-between">
          <div>
            <Title order={2} size="h3">
              Programmes
            </Title>
            <Text size="sm" c="dimmed">
              Drag to reorder (or use the arrows). Unpublished programmes are skipped in the app.
            </Text>
          </div>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save order
          </Button>
        </Group>
        <ProgrammeAddSelect
          label="Add a programme"
          placeholder="Search programmes…"
          nothingFound="No matching programmes"
          exclude={(p) => p.status === 'archived' || items.some((i) => i.programme_id === p.id)}
          onAdd={(p) =>
            setItems((list) => [
              ...list,
              { programme_id: p.id, title: p.title, status: p.status, poster: p.poster },
            ])
          }
        />
        {items.length === 0 ? (
          <EmptyState title="Empty collection">Search above to add programmes.</EmptyState>
        ) : (
          <SortableList
            label="Collection items"
            items={items}
            getId={(i) => i.programme_id}
            getLabel={(i) => i.title}
            onChange={setItems}
            renderItem={(i) => (
              <Group gap="sm" wrap="nowrap">
                <Artwork image={i.poster} title={i.title} width={28} ratio={2 / 3} radius={4} />
                <Anchor
                  component={Link}
                  to={`/programmes/${i.programme_id}`}
                  size="sm"
                  fw={500}
                  c="inherit"
                  truncate
                  style={{ flex: 1 }}
                >
                  {i.title}
                </Anchor>
                <StatusBadge status={i.status} />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${i.title}`}
                  onClick={() =>
                    setItems((list) => list.filter((x) => x.programme_id !== i.programme_id))
                  }
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            )}
          />
        )}
        {dirty && (
          <Text size="xs" c="orange.8">
            Unsaved order changes.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function SettingsForm({ collection }: { collection: AdminCollection }) {
  const qc = useQueryClient();
  const initial = {
    title: collection.title,
    child_title: collection.child_title ?? '',
    slug: collection.slug,
    kind: collection.kind as CollectionKind,
    label_kind: collection.label_kind as LabelKind,
    min_age: collection.min_age as number | string | null,
    max_age: collection.max_age as number | string | null,
    available_from: toDate(collection.available_from),
    available_to: toDate(collection.available_to),
  };
  const form = useForm({
    initialValues: initial,
    validate: {
      title: (v) => (v.trim() ? null : 'Add a title.'),
      available_to: (v, all) =>
        v && all.available_from && v <= all.available_from ? 'Must be after the start.' : null,
    },
  });
  const num = (v: number | string | null) => (v === '' || v == null ? null : Number(v));
  const save = useMutation({
    mutationFn: (v: typeof form.values) =>
      adminApi.updateCollection(collection.id, {
        title: v.title.trim(),
        child_title: v.child_title.trim() || null,
        slug: v.slug.trim(),
        kind: v.kind,
        label_kind: v.label_kind,
        min_age: num(v.min_age),
        max_age: num(v.max_age),
        available_from: toIso(v.available_from),
        available_to: toIso(v.available_to),
      }),
    onSuccess: (c) => {
      qc.setQueryData(qk.collection(c.id), c);
      void qc.invalidateQueries({ queryKey: ['collections'] });
      form.resetDirty();
      notifications.show({ color: 'leaf', message: 'Collection saved.' });
    },
    onError: (e) =>
      notifications.show({ color: 'coral', title: 'Not saved', message: errorMessage(e) }),
  });
  const archive = useMutation({
    mutationFn: () => adminApi.archiveCollection(collection.id),
    onSuccess: (c) => {
      qc.setQueryData(qk.collection(c.id), c);
      void qc.invalidateQueries({ queryKey: ['collections'] });
      notifications.show({ color: 'leaf', message: 'Collection archived.' });
    },
    onError: (e) => notifications.show({ color: 'coral', message: errorMessage(e) }),
  });

  return (
    <Paper withBorder p="md" radius="md" className="pb-surface">
      <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
        <Stack gap="sm">
          <Title order={2} size="h3">
            Settings
          </Title>
          <TextInput
            label="Title"
            description="Shown to grown-ups and in the admin."
            required
            {...form.getInputProps('title')}
          />
          <TextInput
            label="Child-facing title"
            description="Optional, shorter and friendlier."
            {...form.getInputProps('child_title')}
          />
          <TextInput label="Slug" {...form.getInputProps('slug')} />
          <Select
            label="Kind"
            allowDeselect={false}
            data={COLLECTION_KINDS.map(({ value, label }) => ({ value, label }))}
            {...form.getInputProps('kind')}
          />
          <Select
            label="Label"
            description="How the row is labelled in the app, so promotions are never dressed up as picks."
            allowDeselect={false}
            data={LABEL_KINDS}
            {...form.getInputProps('label_kind')}
          />
          <SimpleGrid cols={2}>
            <NumberInput label="Min age" min={0} max={18} {...form.getInputProps('min_age')} />
            <NumberInput label="Max age" min={0} max={18} {...form.getInputProps('max_age')} />
          </SimpleGrid>
          <DateTimePicker
            label="Show from"
            clearable
            valueFormat="D MMM YYYY HH:mm"
            {...form.getInputProps('available_from')}
          />
          <DateTimePicker
            label="Show until"
            clearable
            valueFormat="D MMM YYYY HH:mm"
            {...form.getInputProps('available_to')}
          />
          <Group justify="space-between">
            {!collection.archived ? (
              <Button
                variant="subtle"
                color="coral"
                onClick={() =>
                  modals.openConfirmModal({
                    title: `Archive “${collection.title}”?`,
                    children: (
                      <Text size="sm">
                        It stops showing in the app. Home layout slots that use it must be changed.
                      </Text>
                    ),
                    labels: { confirm: 'Archive', cancel: 'Cancel' },
                    confirmProps: { color: 'coral' },
                    onConfirm: () => archive.mutate(),
                  })
                }
              >
                Archive
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" disabled={!form.isDirty()} loading={save.isPending}>
              Save settings
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}
